import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Radio,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import {
  POPULATE_LEVEL,
  fetchAdgroupsForPopulateModal,
  fetchCampaignsForPopulateModal,
  fetchPortfoliosForPopulateModal,
} from "../../helper/bulkSelection/bulkProductAdUpdate";

const VISIBLE_TAG_LIMIT = 2;

function StatusDot({ status }) {
  const normalized = String(status ?? "").toUpperCase();
  if (!normalized) return null;
  const isEnabled = normalized === "ENABLED";
  return (
    <Box
      title={normalized}
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        flexShrink: 0,
        bgcolor: isEnabled ? "#12B76A" : "#98A2B3",
        alignSelf: "center",
      }}
    />
  );
}

function renderLimitedTags(value, getTagProps, getLabel) {
  const visible = value.slice(0, VISIBLE_TAG_LIMIT);
  const hiddenCount = value.length - visible.length;

  return (
    <>
      {visible.map((option, index) => {
        const { key, ...tagProps } = getTagProps({ index });
        return (
          <Chip
            key={key}
            size="small"
            label={getLabel(option)}
            {...tagProps}
            sx={{ maxWidth: 140 }}
          />
        );
      })}
      {hiddenCount > 0 ? (
        <Chip
          size="small"
          label={`+${hiddenCount}`}
          sx={{
            fontWeight: 700,
            bgcolor: "#F3EFFF",
            color: "#6B4EFF",
          }}
        />
      ) : null}
    </>
  );
}

const LEVEL_OPTIONS = [
  {
    key: POPULATE_LEVEL.RESPECTIVE,
    label: "Respective Campaign & all Ad Groups",
    hint: "Populate into every ad group of each product's own campaign",
    disabledHint: "Group by Campaign or Ad Group to use Respective",
    Icon: AccountTreeRoundedIcon,
    accent: "#6B4EFF",
    accentBg: "#F3EFFF",
  },
  {
    key: POPULATE_LEVEL.CAMPAIGN,
    label: "Campaign Level",
    hint: "Populate into every ad group of the selected campaigns",
    Icon: CampaignRoundedIcon,
    accent: "#12B76A",
    accentBg: "#ECFDF3",
  },
  {
    key: POPULATE_LEVEL.ADGROUP,
    label: "Ad Group Level",
    hint: "Choose campaigns, then ad groups",
    Icon: GroupsRoundedIcon,
    accent: "#F79009",
    accentBg: "#FFFAEB",
  },
];

export default function PopulateProductAdsV2({
  open = false,
  clientId,
  respectiveEnabled = false,
  onClose,
  onSubmit,
}) {
  const [level, setLevel] = useState("");
  const [portfolioOptions, setPortfolioOptions] = useState([]);
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [adgroupOptions, setAdgroupOptions] = useState([]);
  const [selectedPortfolios, setSelectedPortfolios] = useState([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [selectedAdgroups, setSelectedAdgroups] = useState([]);
  const [portfolioInput, setPortfolioInput] = useState("");
  const [campaignInput, setCampaignInput] = useState("");
  const [adgroupInput, setAdgroupInput] = useState("");
  const [isLoadingPortfolios, setIsLoadingPortfolios] = useState(false);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);
  const [isLoadingAdgroups, setIsLoadingAdgroups] = useState(false);

  const needsDestinationPickers =
    level === POPULATE_LEVEL.CAMPAIGN || level === POPULATE_LEVEL.ADGROUP;
  const needsAdgroupDropdown = level === POPULATE_LEVEL.ADGROUP;

  useEffect(() => {
    if (!open) return;
    setLevel("");
    setSelectedPortfolios([]);
    setSelectedCampaigns([]);
    setSelectedAdgroups([]);
    setPortfolioOptions([]);
    setCampaignOptions([]);
    setAdgroupOptions([]);
    setPortfolioInput("");
    setCampaignInput("");
    setAdgroupInput("");
  }, [open]);

  useEffect(() => {
    if (!open || !clientId || !needsDestinationPickers) return;

    let cancelled = false;
    const timer = setTimeout(async () => {
      setIsLoadingPortfolios(true);
      try {
        const rows = await fetchPortfoliosForPopulateModal({
          clientId,
          searchTerm: portfolioInput,
          limit: 200,
        });
        if (!cancelled) setPortfolioOptions(rows);
      } catch (error) {
        console.error(error);
        if (!cancelled) setPortfolioOptions([]);
      } finally {
        if (!cancelled) setIsLoadingPortfolios(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, clientId, needsDestinationPickers, portfolioInput]);

  useEffect(() => {
    if (!open || !clientId || !needsDestinationPickers) return;

    let cancelled = false;
    const timer = setTimeout(async () => {
      setIsLoadingCampaigns(true);
      try {
        const rows = await fetchCampaignsForPopulateModal({
          clientId,
          searchTerm: campaignInput,
          portfolioPlatformIds: selectedPortfolios.map((p) => p.platform_id || p.value),
          limit: 200,
        });
        if (!cancelled) setCampaignOptions(rows);
      } catch (error) {
        console.error(error);
        if (!cancelled) setCampaignOptions([]);
      } finally {
        if (!cancelled) setIsLoadingCampaigns(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, clientId, needsDestinationPickers, campaignInput, selectedPortfolios]);

  useEffect(() => {
    if (!open || !clientId || !needsAdgroupDropdown) return;
    if (!selectedCampaigns.length) {
      setAdgroupOptions([]);
      setSelectedAdgroups([]);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setIsLoadingAdgroups(true);
      try {
        const rows = await fetchAdgroupsForPopulateModal({
          clientId,
          campaignMongoIds: selectedCampaigns.map((c) => c.id || c.value).filter(Boolean),
          searchTerm: adgroupInput,
          limit: 500,
        });
        if (!cancelled) {
          setAdgroupOptions(rows);
          setSelectedAdgroups((prev) =>
            prev.filter((adgroup) => rows.some((row) => row.value === adgroup.value))
          );
        }
      } catch (error) {
        console.error(error);
        if (!cancelled) setAdgroupOptions([]);
      } finally {
        if (!cancelled) setIsLoadingAdgroups(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, clientId, needsAdgroupDropdown, selectedCampaigns, adgroupInput]);

  const isSubmitDisabled = useMemo(() => {
    if (!level) return true;
    if (level === POPULATE_LEVEL.RESPECTIVE) return !respectiveEnabled;
    if (level === POPULATE_LEVEL.CAMPAIGN && !selectedCampaigns.length) return true;
    if (level === POPULATE_LEVEL.ADGROUP && !selectedAdgroups.length) return true;
    return false;
  }, [level, respectiveEnabled, selectedCampaigns, selectedAdgroups]);

  const handleSubmit = () => {
    if (isSubmitDisabled) return;
    const label = LEVEL_OPTIONS.find((option) => option.key === level)?.label || level;
    onSubmit?.({
      level,
      label,
      campaignIds: selectedCampaigns.map((campaign) => campaign.id || campaign.value).filter(Boolean),
      destinations: selectedAdgroups.map((adgroup) => ({
        campaign_id: adgroup.campaign_id,
        adset_id: adgroup.id || adgroup.value,
      })),
      campaignCount: selectedCampaigns.length,
      adgroupCount: selectedAdgroups.length,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableScrollLock
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 16px 48px rgba(16, 24, 40, 0.18)",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          pt: 2.5,
          pb: 1.5,
          mb: 1.5,
          background: "linear-gradient(135deg, #F7F5FF 0%, #FFFFFF 55%)",
          borderBottom: "1px solid",
          borderColor: "grey.200",
        }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#111" }}>
              Populate Product Ads
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "text.secondary", mt: 0.5 }}>
              Choose level and destination campaigns or ad groups.
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 1, pb: 2.5 }}>
        <Stack spacing={2.25}>
          <Box>
            <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#333", mb: 1 }}>
              Populate level
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 1.25,
              }}
            >
              {LEVEL_OPTIONS.map((option) => {
                const isRespective = option.key === POPULATE_LEVEL.RESPECTIVE;
                const disabled = isRespective && !respectiveEnabled;
                const selected = level === option.key;
                const Icon = option.Icon;
                return (
                  <Box
                    key={option.key}
                    onClick={() => {
                      if (disabled) return;
                      setLevel(option.key);
                      if (option.key === POPULATE_LEVEL.RESPECTIVE) {
                        setSelectedPortfolios([]);
                        setSelectedCampaigns([]);
                        setSelectedAdgroups([]);
                      }
                      if (option.key === POPULATE_LEVEL.CAMPAIGN) {
                        setSelectedAdgroups([]);
                      }
                    }}
                    sx={{
                      cursor: disabled ? "not-allowed" : "pointer",
                      opacity: disabled ? 0.55 : 1,
                      border: "1px solid",
                      borderColor: selected ? option.accent : "grey.200",
                      borderRadius: 2,
                      px: 1.5,
                      py: 1.25,
                      bgcolor: selected ? option.accentBg : "#fff",
                      gridColumn: isRespective ? { sm: "1 / -1" } : "auto",
                      transition: "all 0.15s ease",
                      "&:hover": disabled
                        ? {}
                        : {
                            borderColor: option.accent,
                            bgcolor: option.accentBg,
                          },
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <Radio
                        checked={selected}
                        disabled={disabled}
                        size="small"
                        sx={{
                          p: 0.25,
                          color: option.accent,
                          "&.Mui-checked": { color: option.accent },
                        }}
                      />
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "8px",
                          bgcolor: option.accentBg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ fontSize: 16, color: option.accent }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: "13px", fontWeight: 650, color: "#111" }}>
                          {option.label}
                        </Typography>
                        <Typography sx={{ fontSize: "11px", color: "text.secondary", mt: 0.25 }}>
                          {disabled ? option.disabledHint : option.hint}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {needsDestinationPickers ? (
            <>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={portfolioOptions}
                loading={isLoadingPortfolios}
                value={selectedPortfolios}
                inputValue={portfolioInput}
                onInputChange={(_, value) => setPortfolioInput(value)}
                onChange={(_, value) => {
                  setSelectedPortfolios(value);
                  setSelectedCampaigns([]);
                  setSelectedAdgroups([]);
                }}
                getOptionLabel={(option) => option?.label || option?.name || ""}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                renderTags={(value, getTagProps) =>
                  renderLimitedTags(value, getTagProps, (option) => option?.label || option?.name || "")
                }
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option.value}>
                    <Checkbox checked={selected} size="small" sx={{ mr: 0.5 }} />
                    <Typography sx={{ fontSize: "12px" }} noWrap>
                      {option.label}
                    </Typography>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Portfolios (optional)"
                    placeholder="Search portfolios"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingPortfolios ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <Autocomplete
                multiple
                disableCloseOnSelect
                options={campaignOptions}
                loading={isLoadingCampaigns}
                value={selectedCampaigns}
                inputValue={campaignInput}
                onInputChange={(_, value) => setCampaignInput(value)}
                onChange={(_, value) => {
                  setSelectedCampaigns(value);
                  if (level === POPULATE_LEVEL.CAMPAIGN) setSelectedAdgroups([]);
                }}
                getOptionLabel={(option) => option?.label || option?.name || ""}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                renderTags={(value, getTagProps) =>
                  renderLimitedTags(value, getTagProps, (option) => option?.label || option?.name || "")
                }
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option.value}>
                    <Checkbox checked={selected} size="small" sx={{ mr: 0.5 }} />
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                      <StatusDot status={option.status} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: "12px", lineHeight: 1.3 }} noWrap>
                          {option.label}
                        </Typography>
                        <Typography sx={{ fontSize: "10px", color: "text.secondary", lineHeight: 1.3 }} noWrap>
                          ID: {option.platform_id || option.value}
                        </Typography>
                      </Box>
                    </Stack>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Campaigns"
                    placeholder="Search campaigns"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingCampaigns ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </>
          ) : null}

          {needsAdgroupDropdown ? (
            <Autocomplete
              multiple
              disableCloseOnSelect
              disabled={!selectedCampaigns.length}
              options={adgroupOptions}
              loading={isLoadingAdgroups}
              value={selectedAdgroups}
              inputValue={adgroupInput}
              onInputChange={(_, value) => setAdgroupInput(value)}
              onChange={(_, value) => setSelectedAdgroups(value)}
              getOptionLabel={(option) => option?.label || option?.name || ""}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              renderTags={(value, getTagProps) =>
                renderLimitedTags(value, getTagProps, (option) => option?.label || option?.name || "")
              }
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option.value}>
                  <Checkbox checked={selected} size="small" sx={{ mr: 0.5 }} />
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                    <StatusDot status={option.status} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: "12px", lineHeight: 1.3 }} noWrap>
                        {option.label}
                      </Typography>
                      <Typography sx={{ fontSize: "10px", color: "text.secondary", lineHeight: 1.3 }} noWrap>
                        ID: {option.platform_id || option.value}
                      </Typography>
                    </Box>
                  </Stack>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Ad Groups"
                  placeholder={
                    selectedCampaigns.length ? "Search ad groups" : "Select campaigns first"
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingAdgroups ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            textTransform: "none",
            fontSize: "13px",
            fontWeight: 600,
            borderColor: "grey.400",
            color: "#333",
            minWidth: 96,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitDisabled}
          sx={{
            textTransform: "none",
            fontSize: "13px",
            fontWeight: 600,
            minWidth: 140,
            boxShadow: "none",
            bgcolor: "#6B4EFF",
            "&:hover": { bgcolor: "#5A3FE0", boxShadow: "none" },
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
